/**
 * Derived member statistics — personal bests, distance ladder, medals.
 *
 * These are always recomputed from the full result set rather than compared
 * against a stored value, so they cannot drift. SPEC.md §3.9.
 */

import { DISTS, EXPECT_VOL } from './constants';
import { formatTime } from './time';

/*
 * A race result is { id, memberId, eventId?, race, date, dist, timeS, position?, ageCategory? }
 *   date   ISO `YYYY-MM-DD`      dist  one of the DISTS keys
 *   timeS  chip time in whole seconds. Always.
 *
 * A volunteer log entry is { id, memberId, eventId?, role, date, hours, verified }.
 */

/** A member's results, newest first. */
export function memberResults(results, memberId) {
  return results
    .filter((r) => r.memberId === memberId)
    .sort((a, b) => b.date.localeCompare(a.date));
}

/** Fastest result at a distance, or null if they have not run it. */
export function personalBest(results, memberId, dist) {
  const rs = results.filter((r) => r.memberId === memberId && r.dist === dist);
  if (!rs.length) return null;
  return rs.reduce((best, r) => (r.timeS < best.timeS ? r : best));
}

/**
 * Whether a result is that member's best at its distance.
 * `excludeId` lets a freshly saved result compare against every *other* one.
 */
export function isPersonalBest(results, memberId, dist, timeS, excludeId) {
  return results
    .filter((r) => r.memberId === memberId && r.dist === dist && r.id !== excludeId)
    .every((r) => timeS < r.timeS);
}

/** Longest distance completed — this colours the top rule of the bib. */
export function longestDistance(results, memberId) {
  let best = null;
  for (const r of results.filter((x) => x.memberId === memberId)) {
    const d = DISTS.find((x) => x.key === r.dist);
    if (d && (!best || d.km > best.km)) best = d;
  }
  return best;
}

/** The bib class for a member: `t5` | `t10` | `t21` | `t42` | `''`. */
export function bibClass(results, memberId) {
  return longestDistance(results, memberId)?.cls ?? '';
}

/** Total race kilometres logged. */
export function totalKm(results, memberId) {
  return results
    .filter((r) => r.memberId === memberId)
    .reduce((sum, r) => sum + (DISTS.find((d) => d.key === r.dist)?.km ?? 0), 0);
}

/**
 * Completed events that carry a distance — what sits on the club's odometer.
 * A committee meeting is completed too, and contributes nothing.
 */
export function distanceEvents(events) {
  return events.filter((e) => e.completedAt && Number(e.distanceKm) > 0);
}

/**
 * The CLUB's distance: kilometres it has actually put on the road, counted per
 * EVENT and only once the committee marks that event completed.
 *
 * Deliberately NOT the sum of member results (owner's direction, 3 Sep 2026).
 * That figure multiplied every race by its finisher count — a 21K with twelve
 * runners read as 252 km — so it measured roster size, not what the club ran.
 * At 500 members it would be meaningless. Per member, `totalKm` above is still
 * the right number and still powers the bib, PBs and medals.
 */
export function clubDistanceKm(events) {
  return distanceEvents(events).reduce((sum, e) => sum + Number(e.distanceKm), 0);
}

/** Verified volunteer hours, optionally within a single calendar year. */
export function volunteerHours(log, memberId, year) {
  return log
    .filter((v) => v.memberId === memberId && v.verified && (!year || v.date.startsWith(year)))
    .reduce((sum, v) => sum + v.hours, 0);
}

/*
 * A medal is { t, s, on, tone } — tone is the ring finish:
 * 'g' gold, 's' silver, 'b' bronze.
 */

/**
 * The medal cabinet. Unearned medals still render — greyed with a dashed ring,
 * because a locked medal is an invitation.
 */
export function medalsFor(results, log, memberId) {
  const km = totalKm(results, memberId);
  const races = results.filter((r) => r.memberId === memberId).length;
  const hours = volunteerHours(log, memberId);
  const pb = (d) => personalBest(results, memberId, d);
  const at = (d) => {
    const best = pb(d);
    return best ? formatTime(best.timeS) : 'Not run yet';
  };

  return [
    { t: 'First Race', s: races >= 1 ? 'Completed' : 'Run your first race', on: races >= 1, tone: 'b' },
    { t: '5K Club', s: at('5K'), on: !!pb('5K'), tone: 'b' },
    { t: '10K Club', s: at('10K'), on: !!pb('10K'), tone: 's' },
    { t: 'Half Marathon', s: at('21.1K'), on: !!pb('21.1K'), tone: 'g' },
    { t: 'Full Marathon', s: at('42.2K'), on: !!pb('42.2K'), tone: 'g' },
    { t: '100 KM Logged', s: `${km.toFixed(0)} km so far`, on: km >= 100, tone: 's' },
    { t: '5 Races', s: `${races} logged`, on: races >= 5, tone: 's' },
    { t: 'Volunteer', s: `${hours} hrs given`, on: hours >= EXPECT_VOL, tone: 'g' },
  ];
}

/** Initials for an avatar fallback. Never a broken image, never a silhouette. */
export function initials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** A bib number as it appears on a bib: `041`. */
export function bibNo(bib) {
  // A bib is allocated at ACTIVATION, so a pending member genuinely has none.
  // Without this the pay link greeted them with a 58px "null".
  if (bib == null || bib === '') return '—';
  return String(bib).padStart(3, '0');
}

/** Strips everything a phone dialler cannot use. */
export function telHref(phone) {
  return `tel:${String(phone ?? '').replace(/[^0-9+]/g, '')}`;
}
